import {
  ActionIcon,
  Group,
  MultiSelect,
  Paper,
  Select,
  Stack,
  Text,
} from "@mantine/core";
import { IconArrowDown, IconArrowUp, IconTrash } from "@tabler/icons-react";
import { useCallback, useMemo } from "react";
import type { ReflexRuleFormValue } from "./reflexForm";

interface SelectOption {
  value: string;
  label: string;
}

interface ReflexRuleEditorProps {
  index: number;
  value: ReflexRuleFormValue;
  panelOptions: SelectOption[];
  icdOptions: SelectOption[];
  targetError?: string;
  icdError?: string;
  canRemove: boolean;
  canMoveUp: boolean;
  canMoveDown: boolean;
  onChange: (index: number, value: ReflexRuleFormValue) => void;
  onRemove: (index: number) => void;
  onMove: (index: number, direction: -1 | 1) => void;
}

export function ReflexRuleEditor(props: ReflexRuleEditorProps) {
  const {
    index,
    value,
    panelOptions,
    icdOptions,
    targetError,
    icdError,
    canMoveUp,
    canMoveDown,
    canRemove,
    onChange,
    onRemove,
    onMove,
  } = props;
  const label = useMemo(() => `Follow-on rule ${index + 1}`, [index]);
  const handlePanelChange = useCallback(
    (next: string | null) => {
      onChange(index, { ...value, targetPanelDefinitionId: next ?? "" });
    },
    [index, onChange, value]
  );
  const handleCodesChange = useCallback(
    (next: string[]) => {
      onChange(index, { ...value, icdCodeIds: next });
    },
    [index, onChange, value]
  );
  const handleRemove = useCallback(() => onRemove(index), [index, onRemove]);
  const handleMoveUp = useCallback(() => onMove(index, -1), [index, onMove]);
  const handleMoveDown = useCallback(() => onMove(index, 1), [index, onMove]);

  return (
    <Paper withBorder p="md">
      <Stack gap="sm">
        <Group justify="space-between">
          <Text fw={600}>{label}</Text>
          <Group gap="xs">
            <ActionIcon
              size={44}
              variant="subtle"
              aria-label={`Move ${label} up`}
              disabled={!canMoveUp}
              onClick={handleMoveUp}
            >
              <IconArrowUp size={18} />
            </ActionIcon>
            <ActionIcon
              size={44}
              variant="subtle"
              aria-label={`Move ${label} down`}
              disabled={!canMoveDown}
              onClick={handleMoveDown}
            >
              <IconArrowDown size={18} />
            </ActionIcon>
            <ActionIcon
              size={44}
              variant="subtle"
              color="red"
              aria-label={`Remove ${label}`}
              disabled={!canRemove}
              onClick={handleRemove}
            >
              <IconTrash size={18} />
            </ActionIcon>
          </Group>
        </Group>
        <Select
          searchable
          label="Follow-on panel"
          placeholder="Select an active panel"
          withAsterisk
          data={panelOptions}
          value={value.targetPanelDefinitionId}
          error={targetError}
          onChange={handlePanelChange}
        />
        <MultiSelect
          searchable
          clearable
          label="Matching ICD-10 codes"
          description="Matching any selected code on a specimen offers this panel for that specimen."
          placeholder="Search active ICD-10 codes"
          withAsterisk
          data={icdOptions}
          value={value.icdCodeIds}
          error={icdError}
          onChange={handleCodesChange}
        />
      </Stack>
    </Paper>
  );
}
